/**
 * Client-side mirror of the backend password policy.
 *
 * The backend is the authority: `apps/authentication/RegisterView` runs Django's
 * AUTH_PASSWORD_VALIDATORS and returns any failure as `{ error }`. This module exists only so the
 * user gets immediate feedback on the deterministic rules instead of a round trip.
 *
 * Two backend rules are deliberately *not* reimplemented here, because duplicating them would
 * drift and cannot be done faithfully in the browser:
 *   - similarity to username / email / name (Django uses SequenceMatcher)
 *   - Django's list of ~20k common passwords
 * Both surface as a server error on submit.
 *
 * Keep in sync with AUTH_PASSWORD_VALIDATORS in `yona/settings/base.py`.
 */

export const PASSWORD_MIN_LENGTH = 12;

// Mirrors the character set in apps/authentication/validators.py ComplexityValidator.
const SPECIAL_CHARACTER_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export const PASSWORD_HELP_TEXT =
  `At least ${PASSWORD_MIN_LENGTH} characters, with upper and lower case, a number, and a special character`;

function joinReadable(parts) {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * @param {string} password
 * @returns {string|null} a message naming what is missing, or null when the password satisfies
 *   every rule this module can check.
 */
export function describePasswordProblems(password = '') {
  const missing = [];
  if (!/[A-Z]/.test(password)) missing.push('an uppercase letter');
  if (!/[a-z]/.test(password)) missing.push('a lowercase letter');
  if (!/\d/.test(password)) missing.push('a number');
  if (!SPECIAL_CHARACTER_RE.test(password)) missing.push('a special character');

  const tooShort = password.length < PASSWORD_MIN_LENGTH;
  if (!tooShort && missing.length === 0) return null;

  const requirements = [];
  if (tooShort) requirements.push(`be at least ${PASSWORD_MIN_LENGTH} characters`);
  if (missing.length) requirements.push(`include ${joinReadable(missing)}`);

  return `Password must ${joinReadable(requirements)}`;
}
